/**
 * The coin image: geometry, colour and naming.
 *
 * ZERO IMPORTS, per invariant 23. Everything here is arithmetic on numbers and
 * strings and is unit-tested in Node. `drawCoin` is the one browser-only export
 * and takes the context as an argument, so this module still imports nothing —
 * the DOM types are erased at runtime. It is covered by the browser test, not
 * by the unit tests, and that split is deliberate: a canvas polyfill in the
 * test runner would be a native addon, which is exactly what invariant 23
 * exists to keep out of the suite.
 *
 * This produces a FILE. It does not host anything. Hosting is the irreversible
 * half of token metadata (invariant 1: metadata is written before authorities
 * are revoked), and it is not this module's decision to make.
 */

/** Wallets show the token small; 512 is the common denominator and stays sharp. */
export const COIN_SIZE = 512;

export type CoinShape = "circle" | "square";
export type CoinFill = "solid" | "gradient";

export interface CoinStyle {
  ticker: string;
  shape: CoinShape;
  fill: CoinFill;
  colorA: string;
  colorB: string;
  textColor: string;
}

/* ------------------------------------------------------------------ text -- */

/**
 * The glyphs that go on the coin. Uppercase, alphanumerics only, at most five —
 * past that the text is scaled so small it stops reading as a mark. A symbol of
 * nothing usable yields "" and the caller draws a plain disc rather than a box
 * of tofu.
 */
export function normalizeTicker(symbol: string): string {
  return (symbol ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);
}

/**
 * Font size that fits `text` across `size` pixels, allowing for side padding.
 * Linear in the character count, which is what a monospaced-ish fit needs; the
 * cap keeps a single character from swallowing the whole face.
 */
export function fontSizeFor(text: string, size: number = COIN_SIZE): number {
  const n = Math.max(text.length, 1);
  const usable = size * 0.68;
  // 0.62em average advance width for a bold grotesque at these sizes.
  const fitted = usable / (n * 0.62);
  return Math.round(Math.min(fitted, size * 0.42));
}

/* ---------------------------------------------------------------- colour -- */

export function isHexColor(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value ?? "");
}

function channels(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/** WCAG 2.x relative luminance. White is exactly 1, black exactly 0. */
export function relativeLuminance(hex: string): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = channels(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio; 21 for black against white, 1 for a colour on itself. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Black or white, whichever is legible on this background. Not a nicety: the
 * ticker is the only thing that identifies the coin at 32 pixels in a wallet
 * list, and a default that picked white would vanish on every pale palette.
 */
export function readableTextColor(background: string): "#000000" | "#ffffff" {
  return contrastRatio(background, "#000000") >= contrastRatio(background, "#ffffff")
    ? "#000000"
    : "#ffffff";
}

/* --------------------------------------------------------------- palette -- */

/** FNV-1a. Small, deterministic, and does not pretend to be a hash function. */
export function hashSeed(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function hslToHex(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const v = l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return Math.round(255 * v).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * A palette derived from the ticker, so the same ticker always draws the same
 * coin. Reproducible beats random here: an operator who reloads the page and
 * finds a different logo has lost work with no way to get it back.
 */
export function paletteFor(seed: string): { colorA: string; colorB: string } {
  const h = hashSeed(seed || "oxminter");
  const hueA = h % 360;
  // A fixed offset rather than a second hash: adjacent hues make a gradient,
  // two independent hues make a mess.
  const hueB = (hueA + 42) % 360;
  return {
    colorA: hslToHex(hueA, 0.62, 0.46),
    colorB: hslToHex(hueB, 0.66, 0.60),
  };
}

export function defaultStyle(symbol: string): CoinStyle {
  const ticker = normalizeTicker(symbol);
  const { colorA, colorB } = paletteFor(ticker);
  return { ticker, shape: "circle", fill: "gradient", colorA, colorB, textColor: readableTextColor(colorA) };
}

/* ---------------------------------------------------------------- upload -- */

/**
 * The square window to take out of a w×h image, centred. Wallets render the
 * token in a square or a circle; a 16:9 upload dropped in unchanged is
 * letterboxed by some and stretched by others, and the operator cannot tell
 * which until it is on chain.
 */
export function squareCrop(width: number, height: number): { sx: number; sy: number; size: number } {
  const size = Math.min(width, height);
  return { sx: Math.floor((width - size) / 2), sy: Math.floor((height - size) / 2), size };
}

/* ------------------------------------------------------------------ file -- */

/** Same slug rules as the metadata document, so the pair of files match. */
export function coinFileName(name: string, symbol: string): string {
  const base = (name || symbol || "coin")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return `${base || "coin"}-512.png`;
}

/* ------------------------------------------------------------------ draw -- */

/**
 * Draws the coin into a 2D context sized COIN_SIZE square.
 *
 * Browser only. Covered by console/e2e/coin-image.mjs, not by the unit tests —
 * see the note at the top of this file.
 */
export function drawCoin(ctx: CanvasRenderingContext2D, style: CoinStyle, size: number = COIN_SIZE): void {
  ctx.clearRect(0, 0, size, size);

  let paint: string | CanvasGradient = style.colorA;
  if (style.fill === "gradient") {
    const g = ctx.createLinearGradient(0, 0, size, size);
    g.addColorStop(0, style.colorA);
    g.addColorStop(1, style.colorB);
    paint = g;
  }

  ctx.save();
  if (style.shape === "circle") {
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
  }
  ctx.fillStyle = paint;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();

  if (!style.ticker) return;
  const fontPx = fontSizeFor(style.ticker, size);
  ctx.fillStyle = style.textColor;
  ctx.font = `800 ${fontPx}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // A hair below centre: uppercase glyphs sit above the geometric middle.
  ctx.fillText(style.ticker, size / 2, size / 2 + fontPx * 0.04);
}
